import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Clock, User } from 'lucide-react';
import type { ActiveFlag } from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import { api, ApiError } from '../../lib/api';
import { resolveMeContext, todayIso, type MeProgramme } from '../../lib/meContext';
import { formatTime } from '../../lib/dateFmt';
import { OnboardingTasks } from '../../components/onboarding/OnboardingTasks';
import { TrainingTasks } from '../../components/training/TrainingTasks';
import { FlagBanner } from '../../components/person/FlagBanner';
import { PreflightCard, type Preflight } from '../../components/person/PreflightCard';
import { DocumentsCard } from '../../components/me/DocumentsCard';
import { GroupDivider } from '../../components/me/GroupDivider';
import { IssuedItemsCard } from '../../components/me/IssuedItemsCard';
import { KitToCollectCard } from '../../components/me/KitToCollectCard';
import { MyHandoversCard } from '../../components/me/MyHandoversCard';
import { ProgressCard } from '../../components/me/ProgressCard';
import { TasksCard } from '../../components/me/TasksCard';
import type {
  IssuedItem,
  MeDocument,
  MeTask,
  OutstandingItem,
  StationProgress,
} from '../../components/me/meTypes';
import { EmptyState } from '../../components/ui/EmptyState';
import { Hero } from '../../components/ui/Hero';
import { Pill } from '../../components/ui/Pill';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { SkeletonList } from '../../components/ui/Skeleton';

interface MeView {
  programme: MeProgramme | null;
  person: {
    id: string;
    full_name: string;
    first_name: string;
    email: string;
    position: string | null;
    manager: string | null;
    contract: string | null;
    start_date: string | null;
  };
  state: {
    arrived: boolean;
    arrived_at: string | null;
    flags: ActiveFlag[];
  };
  station_progress: StationProgress[];
  issued: IssuedItem[];
  outstanding: OutstandingItem[];
  documents: MeDocument[];
  tasks: MeTask[];
  preflight: Preflight | null;
}

/**
 * Inductee self-service (CLAUDE.md 6.4). Their own checklist,
 * flags, signed documents, and PPE sizes-on-record. The cards live
 * in components/me/; this route only fetches and composes them.
 */
export function SelfService() {
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const me = useQuery({
    queryKey: ['me-view'],
    enabled: !!user?.person_id,
    queryFn: () => api<MeView>('/api/me'),
  });

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  // Session gone (expiry, network blip, logout in another tab): bounce
  // straight to the branded login page, same as Home, rather than
  // stranding the inductee on a bare "please sign in" stub.
  if (!user) return <Navigate to="/login" replace />;
  if (!user.person_id) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        This page is for attendees. Your account is staff-only; head to{' '}
        <Link to="/" className="underline">home</Link>.
      </main>
    );
  }
  if (me.isLoading) {
    return (
      <main className="min-h-screen bg-bg p-6">
        <div className="mx-auto max-w-3xl space-y-3">
          <SkeletonList rows={4} />
        </div>
      </main>
    );
  }
  if (me.error) {
    // Archived / deleted mid-session: the account is closed, not a
    // transient fault, so show the contact-HR message rather than a retry.
    if (me.error instanceof ApiError && me.error.code === 'AccountInactiveError') {
      return (
        <main className="min-h-screen bg-bg p-6">
          <EmptyState
            icon={<AlertCircle className="h-6 w-6" />}
            title="This account is no longer active"
            description="Your access to onboarding has been closed. If you think this is a mistake, please contact HR."
          />
        </main>
      );
    }
    const status = me.error instanceof ApiError ? me.error.status : 0;
    return (
      <main className="min-h-screen bg-bg p-6">
        <EmptyState
          icon={<AlertCircle className="h-6 w-6" />}
          title="Couldn't load your details"
          description={`The server returned ${status}. Try again in a moment.`}
        />
      </main>
    );
  }
  if (!me.data) return null;

  const v = me.data;
  const ctx = resolveMeContext(v.programme, todayIso());
  const doneStations = v.station_progress.filter((s) => s.status === 'done').length;
  const totalRelevantStations = v.station_progress.filter(
    (s) => s.status !== 'not_required',
  ).length;

  // Everything below the onboarding/tasks block (kit, signatures, documents)
  // happens at a desk on the day, not ahead of time. We fence it off under a
  // header so the inductee knows it is not theirs to action now. A real dated
  // mobilisation gets the "on the day" framing; a plain new starter gets the
  // start-day framing, and the station-by-station progress card is dropped
  // because stations only exist on a mobilisation, not a normal induction.
  const hasMobilisation = v.programme?.kind === 'mobilisation';
  const dayGroupTitle = hasMobilisation
    ? 'On the day of the mobilisation'
    : 'On your start day';
  const dayGroupHint = hasMobilisation
    ? 'Our team completes these with you on the day. Nothing for you to action now.'
    : 'Kit and documents are sorted on your first day. Nothing to do ahead of time.';

  // Mirror header for the work the inductee does themselves ahead of time
  // (forms, documents, to-dos), so it's clear what is theirs to action now
  // versus what gets sorted at a desk later. Only shown when there is any.
  const hasBeforeWork = v.tasks.length > 0;
  const beforeGroupTitle = hasMobilisation
    ? 'Complete before the mobilisation'
    : 'Complete before your start date';
  const beforeGroupHint = 'These are yours to complete online, ahead of time.';

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl space-y-5 px-4 py-8 sm:px-6 sm:py-10">
        <Hero
          eyebrow={
            <>
              <User className="h-3 w-3" />
              <span>{ctx.eyebrow}</span>
            </>
          }
          title={`Hi ${v.person.first_name}`}
          description={
            <span className="inline-flex flex-wrap items-center gap-2">
              <span>{v.person.position ?? 'Position unknown'}</span>
              {v.person.contract ? (
                <>
                  <span>·</span>
                  <span>{v.person.contract}</span>
                </>
              ) : null}
              {v.state.arrived ? (
                <Pill tone="success" icon={<CheckCircle2 className="h-3 w-3" />}>
                  Arrived{' '}
                  {formatTime(v.state.arrived_at!)}
                </Pill>
              ) : (
                <Pill tone="neutral" icon={<Clock className="h-3 w-3" />}>
                  Not yet arrived
                </Pill>
              )}
            </span>
          }
        >
          {hasMobilisation && totalRelevantStations > 0 ? (
            <>
              <div className="mb-2 flex items-center justify-between text-xs text-fg-muted">
                <span>Stations complete</span>
                <span className="pv-num font-semibold">
                  {doneStations} / {totalRelevantStations}
                </span>
              </div>
              <ProgressBar
                value={doneStations}
                total={totalRelevantStations}
                ariaLabel="Station completion"
              />
            </>
          ) : null}
        </Hero>

        {hasBeforeWork ? (
          <GroupDivider title={beforeGroupTitle} hint={beforeGroupHint} />
        ) : null}

        <OnboardingTasks tasks={v.tasks} startDate={v.person.start_date} />

        {/* Training is onboarding work too, but it reads as its own thing:
            courses to sit, not forms to fill in. */}
        <TrainingTasks tasks={v.tasks} />

        {v.state.flags.length > 0 ? (
          <FlagBanner flags={v.state.flags} />
        ) : null}

        {/* Onboarding form/document/upload tasks render in "Your onboarding"
            above; "Your tasks" is only for plain to-do tasks, so a form
            doesn't appear twice (and never with the wrong submit-photo UI). */}
        <TasksCard tasks={v.tasks.filter((t) => !t.onboarding_kind)} />

        <GroupDivider title={dayGroupTitle} hint={dayGroupHint} />

        {hasMobilisation ? <ProgressCard rows={v.station_progress} /> : null}

        {v.preflight ? (
          <PreflightCard preflight={v.preflight} tasks={v.tasks} />
        ) : null}

        <KitToCollectCard
          outstanding={v.outstanding}
          stationProgress={v.station_progress}
          preview={ctx.mode === 'onboarding'}
        />

        <IssuedItemsCard issued={v.issued} />

        <MyHandoversCard />

        <DocumentsCard
          documents={v.documents}
          personName={v.person.first_name}
        />
      </div>
    </main>
  );
}
